import { useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import { useQuery, useMutation } from "convex/react";
import { api } from "convex/_generated/api";
import type { Id } from "convex/_generated/dataModel";
import { UserButton } from "@clerk/clerk-react";
import { Plus, Calendar, Shield, Clock, Repeat, Pencil, Building2, Check, KeyRound, MapPin, ExternalLink } from "lucide-react";
import { PageContainer } from "../components/layout/PageContainer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { formatTimeRange } from "../lib/dateUtils";
import { EventStyleBadges } from "../components/calendar/DanceStyleBadge";
import { EventEditModal } from "../components/forms/EventEditModal";
import { cn } from "../lib/utils";
import type { Doc } from "convex/_generated/dataModel";

const FREQUENCY_LABELS: Record<string, string> = {
  weekly: "Weekly",
  biweekly: "Every other week",
  monthly: "Monthly",
  first_weekday_of_month: "Monthly (first weekday)",
  last_weekday_of_month: "Monthly (last weekday)",
};

const STATUS_BADGE: Record<string, { label: string; className: string }> = {
  approved: { label: "Approved", className: "bg-green-500/10 text-green-400" },
  pending:  { label: "Pending",  className: "bg-yellow-500/10 text-yellow-400" },
  hidden:   { label: "Hidden",   className: "bg-red-500/10 text-red-400" },
  archived: { label: "Archived", className: "bg-muted text-muted-foreground" },
};

const VERIFICATION_STATUS = {
  unverified: { label: "Unverified", className: "text-muted-foreground" },
  pending: { label: "Verification Pending", className: "text-yellow-400" },
  verified: { label: "Verified", className: "text-green-400" },
  trusted: { label: "Trusted", className: "text-primary" },
} as const;

export function DashboardPage() {
  const { user, isLoading } = useCurrentUser();
  const [, navigate] = useLocation();
  const myEvents = useQuery(api.users.getMyEvents);
  const myRecurring = useQuery(api.recurring.getMyRecurringPatterns);
  const myStudios = useQuery(api.studioOrganizers.getMyStudios);
  const myVenueAuths = useQuery(api.venueOrganizers.getMyVenueAuthorizations);
  const allVenues = useQuery(api.venues.getAllVenues, {});
  const requestVenueAccess = useMutation(api.venueOrganizers.requestVenueAccess);
  const requestVerification = useMutation(api.users.requestVerification);
  const acceptInvite = useMutation(api.studioOrganizers.acceptInvite);
  const declineInvite = useMutation(api.studioOrganizers.declineInvite);
  const joinByCode = useMutation(api.studioOrganizers.joinStudioByCode);
  const [verifyUrl, setVerifyUrl] = useState("");
  const [verifyError, setVerifyError] = useState("");
  const [verifySubmitted, setVerifySubmitted] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Doc<"events"> | null>(null);
  const [joinCode, setJoinCode] = useState("");
  const [joinError, setJoinError] = useState<string | null>(null);
  const [joinSuccess, setJoinSuccess] = useState<string | null>(null);
  const [venueAccessOpen, setVenueAccessOpen] = useState(false);
  const [venueAccessVenueId, setVenueAccessVenueId] = useState<string>("");
  const [venueAccessUrl, setVenueAccessUrl] = useState("");
  const [venueAccessNotes, setVenueAccessNotes] = useState("");
  const [venueAccessError, setVenueAccessError] = useState<string | null>(null);
  const [venueAccessSuccess, setVenueAccessSuccess] = useState(false);
  const [venueAccessBusy, setVenueAccessBusy] = useState(false);

  const handleRequestVenueAccess = async () => {
    if (!venueAccessVenueId) return;
    setVenueAccessBusy(true);
    setVenueAccessError(null);
    try {
      await requestVenueAccess({
        venueId: venueAccessVenueId as Id<"venues">,
        evidenceUrl: venueAccessUrl.trim() || undefined,
        notes: venueAccessNotes.trim() || undefined,
      });
      setVenueAccessSuccess(true);
      setVenueAccessOpen(false);
      setVenueAccessVenueId("");
      setVenueAccessUrl("");
      setVenueAccessNotes("");
    } catch (e: any) {
      setVenueAccessError(e.message ?? "Something went wrong");
    } finally {
      setVenueAccessBusy(false);
    }
  };

  const handleJoinByCode = async () => {
    if (!joinCode.trim()) return;
    setJoinError(null);
    setJoinSuccess(null);
    try {
      const result = await joinByCode({ inviteCode: joinCode.trim().toUpperCase() });
      setJoinCode("");
      setJoinSuccess(`Joined ${result.studioName}!`);
      setTimeout(() => setJoinSuccess(null), 4000);
    } catch (e: any) {
      setJoinError(e.message ?? "Invalid invite code.");
    }
  };

  const handleRequestVerification = async () => {
    try {
      setVerifyError("");
      await requestVerification({ externalUrl: verifyUrl.trim() || undefined });
      setVerifySubmitted(true);
    } catch (e: any) {
      setVerifyError(e.message ?? "Something went wrong");
    }
  };

  useEffect(() => {
    if (!isLoading && !user) navigate("/onboarding");
  }, [isLoading, user]);

  if (isLoading) {
    return (
      <PageContainer>
        <div className="py-12 text-center text-muted-foreground">Loading...</div>
      </PageContainer>
    );
  }

  if (!user) return null;

  // Studio accounts have their own dedicated portal at /studio
  if (user.accountType === "studio") {
    navigate("/studio");
    return null;
  }

  const verificationInfo = VERIFICATION_STATUS[user.verificationStatus];

  return (
    <PageContainer>
      {/* Profile header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <UserButton />
          <div>
            <h1 className="font-display text-2xl text-foreground">
              {user.name || user.email}
            </h1>
            <div className="flex items-center gap-2 text-sm">
              <span className="capitalize text-muted-foreground">
                {user.accountType}
              </span>
              <span className="text-border">|</span>
              <span className={cn("flex items-center gap-1", verificationInfo.className)}>
                <Shield className="w-3.5 h-3.5" />
                {verificationInfo.label}
              </span>
            </div>
          </div>
        </div>
        <Button asChild size="sm" variant="contrast">
          <Link href="/submit">
            <Plus className="w-4 h-4" />
            Submit Event
          </Link>
        </Button>
      </div>

      {/* Verification Request */}
      {/* Verification is organizer-only: it gates venue-level posting
          authorization, which dancers cannot request. Dancer submissions are
          always reviewed, so there is nothing for them to gain here. */}
      {user.accountType !== "dancer" &&
        (user.verificationStatus === "unverified" ||
          user.verificationStatus === "pending") && (
        <section className="mb-8 bg-card rounded-xl border border-border/50 p-5">
          <h2 className="font-semibold text-base text-foreground mb-1 flex items-center gap-2">
            <Shield className="w-4 h-4" />
            Get Verified
          </h2>
          {user.verificationStatus === "pending" ? (
            <p className="text-sm text-yellow-400">
              Your verification request is pending review.
            </p>
          ) : verifySubmitted ? (
            <p className="text-sm text-green-400">
              Verification request submitted! An admin will review it soon.
            </p>
          ) : (
            <>
              <p className="text-sm text-muted-foreground mb-3">
                Verification lets admins confirm your identity. Once verified, you can request authorization to post at specific venues without review. Optionally provide a link to your website or social profile.
              </p>
              <div className="flex gap-2">
                <Input
                  value={verifyUrl}
                  onChange={(e) => setVerifyUrl(e.target.value)}
                  placeholder="https://your-website.com (optional)"
                  className="flex-1"
                />
                <Button size="sm" onClick={handleRequestVerification}>
                  Request Verification
                </Button>
              </div>
              {verifyError && (
                <p className="text-xs text-destructive mt-2">{verifyError}</p>
              )}
            </>
          )}
        </section>
      )}

      {/* My Studios (organizer accounts) */}
      {user.accountType === "organizer" && (
        <section className="mb-8">
          <h2 className="font-semibold text-lg text-foreground mb-4 flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            My Studios
          </h2>

          {/* Pending invites */}
          {myStudios && myStudios.pending.length > 0 && (
            <div className="mb-4 space-y-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Pending Invites
              </p>
              {myStudios.pending.map((record) => (
                <div
                  key={record._id}
                  className="flex items-center justify-between bg-card rounded-lg border border-amber-200 dark:border-amber-800/50 px-4 py-3"
                >
                  <div>
                    <p className="text-sm font-medium text-foreground">{record.studioName}</p>
                    {record.studioAddress && (
                      <p className="text-xs text-muted-foreground">{record.studioAddress}</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-destructive border-destructive/30 hover:border-destructive text-xs"
                      onClick={() => declineInvite({ studioOrganizerId: record._id })}
                    >
                      Decline
                    </Button>
                    <Button
                      size="sm"
                      className="text-xs"
                      onClick={() => acceptInvite({ studioOrganizerId: record._id })}
                    >
                      <Check className="w-3.5 h-3.5" />
                      Accept
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Active studios */}
          {myStudios && myStudios.active.length > 0 ? (
            <div className="space-y-2 mb-4">
              {myStudios.active.map((record) => (
                <div
                  key={record._id}
                  className="flex items-center gap-3 bg-card rounded-lg border border-border/50 px-4 py-3"
                >
                  <Building2 className="w-4 h-4 text-primary flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-foreground">{record.studioName}</p>
                    {record.studioAddress && (
                      <p className="text-xs text-muted-foreground">{record.studioAddress}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            myStudios?.pending.length === 0 && (
              <p className="text-sm text-muted-foreground mb-4">
                You're not affiliated with any studios yet.
              </p>
            )
          )}

          {/* Venue access requests */}
          <div className="bg-card rounded-xl border border-border/50 p-4 mb-3">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-foreground flex items-center gap-2">
                <MapPin className="w-4 h-4 text-primary" />
                Venue Access Authorizations
              </p>
              <button
                onClick={() => { setVenueAccessOpen((v) => !v); setVenueAccessError(null); setVenueAccessSuccess(false); }}
                className="text-xs text-primary hover:underline"
              >
                {venueAccessOpen ? "Cancel" : "Request access"}
              </button>
            </div>

            {/* Existing authorizations */}
            {myVenueAuths && myVenueAuths.length > 0 && (
              <div className="mb-3 space-y-1.5">
                {myVenueAuths.map((r) => (
                  <div key={r._id} className="flex items-center justify-between text-sm">
                    <span className="text-foreground">{r.venue?.name ?? "Unknown venue"}</span>
                    <span className={cn("text-xs px-2 py-0.5 rounded-full", {
                      "bg-green-500/10 text-green-400": r.status === "approved",
                      "bg-yellow-500/10 text-yellow-400": r.status === "pending",
                      "bg-red-500/10 text-red-400": r.status === "rejected",
                    })}>
                      {r.status === "approved" ? "Approved" : r.status === "pending" ? "Pending review" : "Rejected"}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {venueAccessSuccess && (
              <p className="text-xs text-green-400 mb-2">Request submitted! An admin will review it soon.</p>
            )}

            {venueAccessOpen && (
              <div className="space-y-2 pt-1">
                <select
                  value={venueAccessVenueId}
                  onChange={(e) => setVenueAccessVenueId(e.target.value)}
                  className="w-full text-sm rounded-md border border-border bg-background px-3 py-2 text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  <option value="">Select a venue…</option>
                  {(allVenues ?? [])
                    .filter((v) => !myVenueAuths?.some((a) => a.venueId === v._id && a.status !== "rejected"))
                    .map((v) => (
                      <option key={v._id} value={v._id}>{v.name}</option>
                    ))}
                </select>
                <input
                  type="url"
                  value={venueAccessUrl}
                  onChange={(e) => setVenueAccessUrl(e.target.value)}
                  placeholder="Link to evidence (website, social, etc.) — optional"
                  className="w-full text-sm rounded-md border border-border bg-background px-3 py-2 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                />
                <textarea
                  value={venueAccessNotes}
                  onChange={(e) => setVenueAccessNotes(e.target.value)}
                  placeholder="Describe your role at this venue (e.g. guest instructor, regular social host…)"
                  rows={2}
                  className="w-full text-sm rounded-md border border-border bg-background px-3 py-2 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary resize-none"
                />
                {venueAccessError && (
                  <p className="text-xs text-destructive">{venueAccessError}</p>
                )}
                <button
                  onClick={handleRequestVenueAccess}
                  disabled={!venueAccessVenueId || venueAccessBusy}
                  className="w-full text-sm bg-primary text-primary-foreground rounded-md py-2 disabled:opacity-50 hover:bg-primary/90 transition-colors"
                >
                  {venueAccessBusy ? "Submitting…" : "Submit Request"}
                </button>
              </div>
            )}
          </div>

          {/* Join by code */}
          <div className="bg-card rounded-xl border border-border/50 p-4">
            <p className="text-sm font-medium text-foreground mb-2 flex items-center gap-2">
              <KeyRound className="w-4 h-4 text-primary" />
              Join a Studio with Invite Code
            </p>
            <div className="flex gap-2">
              <Input
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                placeholder="XXXXXXXX"
                className="font-mono uppercase tracking-widest"
                onKeyDown={(e) => e.key === "Enter" && handleJoinByCode()}
              />
              <Button size="sm" onClick={handleJoinByCode} disabled={!joinCode.trim()}>
                Join
              </Button>
            </div>
            {joinError && <p className="text-xs text-destructive mt-2">{joinError}</p>}
            {joinSuccess && <p className="text-xs text-emerald-500 mt-2">{joinSuccess}</p>}
          </div>
        </section>
      )}

      {/* My Events */}
      <section>
        <h2 className="font-semibold text-lg text-foreground mb-4 flex items-center gap-2">
          <Calendar className="w-5 h-5" />
          My Events
        </h2>

        {myEvents === undefined ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-20 bg-secondary/30 rounded-lg animate-pulse" />
            ))}
          </div>
        ) : myEvents.length === 0 ? (
          <div className="text-center py-8 bg-card rounded-xl border border-border/50">
            <p className="text-muted-foreground mb-3">You haven't submitted any events yet.</p>
            <Button asChild variant="outline" size="sm">
              <Link href="/submit">Submit Your First Event</Link>
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {myEvents.map((event) => {
              const statusInfo = STATUS_BADGE[event.status] ?? { label: event.status, className: "bg-muted text-muted-foreground" };
              return (
                <div
                  key={event._id}
                  className="bg-card rounded-xl border border-border/50 p-4 flex items-center gap-4"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium text-foreground truncate">
                        {event.title}
                      </h3>
                      <span
                        className={cn(
                          "text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0",
                          statusInfo.className
                        )}
                      >
                        {statusInfo.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        {event.date} {formatTimeRange(event.startTime, event.endTime)}
                      </span>
                      <span>{event.venueName}</span>
                    </div>
                    <div className="flex flex-wrap gap-1 mt-2">
                      <EventStyleBadges danceStyles={event.danceStyles} mainStyles={event.mainStyles} />
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditingEvent(event)}
                    className="shrink-0"
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </section>
      {/* Recurring Events */}
      {myRecurring && myRecurring.length > 0 && (
        <section className="mt-8">
          <h2 className="font-semibold text-lg text-foreground mb-4 flex items-center gap-2">
            <Repeat className="w-5 h-5" />
            My Recurring Events
          </h2>
          <div className="space-y-3">
            {myRecurring.map(({ pattern, template }) => (
              <div
                key={pattern._id}
                className="bg-card rounded-xl border border-border/50 p-4"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium text-foreground truncate">{template.title}</h3>
                      <span
                        className={cn(
                          "text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0",
                          pattern.isActive
                            ? "bg-green-500/10 text-green-400"
                            : "bg-red-500/10 text-red-400"
                        )}
                      >
                        {pattern.isActive ? "Active" : "Inactive"}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-muted-foreground mb-2">
                      <span className="flex items-center gap-1">
                        <Repeat className="w-3.5 h-3.5" />
                        {FREQUENCY_LABELS[pattern.frequency] ?? pattern.frequency}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        {formatTimeRange(template.startTime, template.endTime)}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mb-2">{template.venueName}</p>
                    <div className="flex flex-wrap gap-1">
                      <EventStyleBadges danceStyles={template.danceStyles} mainStyles={template.mainStyles} />
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditingEvent(template)}
                    className="shrink-0"
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {editingEvent && (
        <EventEditModal
          event={editingEvent}
          onClose={() => setEditingEvent(null)}
          onSaved={() => setEditingEvent(null)}
        />
      )}
    </PageContainer>
  );
}
